import React from 'react';
import { Radio, Activity } from 'lucide-react';

const EVENT_LABELS = {
  'booking.created': 'Bookings created',
  'booking.confirmed': 'Bookings confirmed',
  'payment.completed': 'Payments completed',
  'notification.dispatched': 'Notifications sent',
  'vendor.profile_updated': 'Vendor profiles updated',
  'audit.payout.recorded': 'Vendor payout recorded',
  'audit.booking.date_change.approved': 'Booking date change approved',
  'audit.booking.date_change.rejected': 'Booking date change rejected',
  'audit.vendor.verification.approved': 'Vendor verification approved',
  'audit.vendor.verification.rejected': 'Vendor verification rejected',
  'analytics.booking.created': 'Analytics: booking created',
  'analytics.booking.confirmed': 'Analytics: booking confirmed',
  'analytics.payment.completed': 'Analytics: payment completed',
};

const formatEventType = (eventType) =>
  EVENT_LABELS[eventType] || eventType.replace(/[._]/g, ' ');

const EventStreamPanel = ({ eventStream }) => {
  const totals = eventStream?.totals_by_type || [];
  const daily = eventStream?.daily || [];
  const totalEvents = eventStream?.total_events || 0;
  const maxDaily = Math.max(...daily.map((d) => d.total), 1);

  return (
    <div className="bg-white border rounded-lg p-3 sm:p-4">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Radio className="w-5 h-5 text-primary-600" />
            Platform event stream
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Counts from the outbox pipeline (Kafka consumer or log driver + outbox:publish).
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total events</p>
          <p className="text-2xl font-bold text-gray-900">{totalEvents}</p>
        </div>
      </div>

      {totals.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
          <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          No events recorded for this period yet. Create a booking or run{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">php artisan outbox:publish</code>.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By event type</h3>
            <div className="space-y-2">
              {totals.map((row) => (
                <div key={row.event_type} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{formatEventType(row.event_type)}</span>
                  <span className="font-semibold text-gray-900 tabular-nums">{row.count}</span>
                </div>
              ))}
            </div>
          </div>

          {daily.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Daily volume</h3>
              <div className="space-y-2">
                {daily.map((day) => (
                  <div key={day.date} className="flex items-center gap-3 text-sm">
                    <span className="w-24 shrink-0 text-gray-500 tabular-nums">{day.date}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full"
                        style={{ width: `${Math.max((day.total / maxDaily) * 100, 4)}%` }}
                      />
                    </div>
                    <span className="w-8 text-right font-medium tabular-nums">{day.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventStreamPanel;
